import React, { useEffect, useRef } from "react";
import { Alert, AlertTitle, Box, BoxProps } from "@mui/material";
import { apiClient, apiClientHooks } from "../bootstrapping/InitApiClient";
import { queryClient } from "../bootstrapping/InitReactQuery";
import GoToDashboardButton from "../components/GoToDashboardButton";

const ApplicationPaymentStatusChecker: React.FC<{ application_id: string } & BoxProps> = ({ application_id, ...boxProps }) => {
  // Do we have any existing stripe payments for this application?
  // If so, we should check if they are paid or not.
  const stripeCheckoutForEBulkApplicationByID = apiClientHooks.useStripeCheckoutsExistingForEBulkApplicationByID({
    params: {
      id: application_id,
    },
  })
  console.log('[ApplicationPayment.existingStripeCheckout:data]', stripeCheckoutForEBulkApplicationByID.data?.checkouts)

  const [existingStripeCheckout, setExistingStripeCheckout] = React.useState<{ status: 'paid' } | { status: 'other', checkoutSessionIDs: string[] } | undefined>(undefined)
  useEffect(() => {
    // Wait for the data to be loaded
    if (!stripeCheckoutForEBulkApplicationByID.data) return

    // If there are existing Stripe checkouts
    if (stripeCheckoutForEBulkApplicationByID.data?.checkouts?.length) {
      // If any are paid, we set the notice status to 'paid' and stop
      // If this is the case, something has gone wrong, as this application should not still be in the awaiting_payment status.
      const isPaid = stripeCheckoutForEBulkApplicationByID.data.checkouts.some(checkout => checkout.payment_status === 'paid')
      if (isPaid) {
        console.log('[ApplicationPayment.existingStripeCheckout:isPaid] Existing Stripe Checkout is paid')
        setExistingStripeCheckout({ status: 'paid' })
        return
      }

      // Otherwise we extract the Stripe Session ID from each of the checkouts
      const checkoutSessionIDs = stripeCheckoutForEBulkApplicationByID.data.checkouts.map(checkout => checkout.id)
      console.log('[ApplicationPayment.existingStripeCheckout:checkoutSessionIDs]', checkoutSessionIDs)
      setExistingStripeCheckout({ status: 'other', checkoutSessionIDs })
    }
  }, [stripeCheckoutForEBulkApplicationByID.data])

  const checkedSessionIDs = useRef<string[]>([])
  useEffect(() => {
    if (existingStripeCheckout?.status === 'other') {
      // Filter out any session IDs that we've already checked
      const sessionIDsToCheck = existingStripeCheckout.checkoutSessionIDs.filter(sessionID => !checkedSessionIDs.current.includes(sessionID))
      if (sessionIDsToCheck.length === 0) {
        console.log('[ApplicationPayment.existingStripeCheckout:noNewSessionsToCheck]')
        return // No new session IDs to check
      }

      for (const sessionID of sessionIDsToCheck) {
        checkedSessionIDs.current.push(sessionID)

        console.log('[ApplicationPayment.existingStripeCheckout:stripeStatusCall]', { sessionID, application_id })
        apiClient.stripeStatusForEBulkApplicationByID({
          params: {
            id: application_id,
          },
          queries: {
            session_id: sessionID,
          },
        }).then(stripeStatus => {
          console.log('[ApplicationPayment.existingStripeCheckout:stripeStatusResponse]', stripeStatus)
          if (stripeStatus.payment_status === 'paid') {
            // If the payment is paid, we set the notice status to 'paid'
            setExistingStripeCheckout({ status: 'paid' })
            // Invalidate the queries so that the application can be updated
            queryClient.invalidateQueries({ queryKey: apiClientHooks.getKeyByAlias('getEBulkApplicationByID') })
            queryClient.invalidateQueries({ queryKey: apiClientHooks.getKeyByAlias('getEBulkApplications') })
            queryClient.invalidateQueries({ queryKey: apiClientHooks.getKeyByAlias('getGlobalStats') })
            queryClient.invalidateQueries({ queryKey: apiClientHooks.getKeyByAlias('priceForEBulkApplicationByID') })
            queryClient.invalidateQueries({ queryKey: apiClientHooks.getKeyByAlias('payForEBulkApplicationByID') })
          }
        }).catch(error => {
          console.error('[ApplicationPayment.existingStripeCheckout:stripeStatusError]', error)
        })
      }
    }
  }, [existingStripeCheckout, application_id])

  return (
    <Box {...boxProps}>
      {existingStripeCheckout?.status === 'paid' && (
        <Alert severity="success">
          <AlertTitle>Payment Successful</AlertTitle>
          Your payment has been successfully processed. The application is now ready for countersigning.
          <GoToDashboardButton variant="contained" sx={{ marginTop: 2 }}>
            Return to Dashboard
          </GoToDashboardButton>
        </Alert>
      )}
      {existingStripeCheckout?.status === 'other' && (
        <Alert severity="info">
          <AlertTitle>Payment Pending</AlertTitle>
          It appears that you tried to pay for this application using Stripe previously, but the payment has not yet been confirmed. Please contact us if you believe you have paid for this application.
        </Alert>
      )}
    </Box>
  )
}

export default ApplicationPaymentStatusChecker;
